import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, ChevronDown, Loader2, RotateCcw, X } from 'lucide-react';
import clsx from 'clsx';
import { ENDPOINTS, getAuthHeaders, handleAuthError } from './config';
import useDialogA11y from './useDialog';
import { HistoryEventList, describeActor, formatHistoryStamp } from './expenseHistory';
import { describeEntryType, formatMoney } from './expenseFormat';

/**
 * Recently deleted — the recovery path for a soft-deleted transaction.
 *
 * Why this exists at all: deleting a transaction has always been a soft delete,
 * so the row survives in the database. But the only handle on it was the record
 * id held by the five-second Undo toast. The toast expired, the reference was
 * dropped 5.1s later, a reload lost it outright, and every ledger read filters
 * soft-deleted rows out — so a reversible operation behaved, for the two people
 * whose money this is, as permanent. A missed toast is not consent.
 *
 * Three deliberate constraints:
 *
 *  1. COMPACT. This is a drawer reached from one small link in the ledger
 *     header, not a second dashboard. The ledger itself is unchanged: no
 *     "show deleted" checkbox, no greyed-out ghost rows, no extra column.
 *  2. NO PERMANENT DELETE. There is no purge control here and there must not
 *     be one. The only reason a deleted row is still readable is that nothing
 *     in the product can destroy it.
 *  3. ONE ENTRY PER RECOVERABLE UNIT. A currency conversion is two rows that
 *     only balance as a pair; the server groups them, and restoring the entry
 *     revives both legs in one transaction. Two Restore buttons would offer
 *     the same restore twice and imply half a conversion can come back.
 */

// Which timestamp to trust for "deleted when". The audit event's `occurred_at`
// is timezone-aware Pacific; the record's `deleted_at` is a naive local stamp.
// Prefer the one that can be placed on a real timeline.
const deletionStamp = (entry) => entry.deleted_by?.occurred_at || entry.deleted_at;

const DeletedEntry = ({ entry, onRestore, restoring, disabled }) => {
  const [confirming, setConfirming] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState('');
  const restoreButtonRef = useRef(null);

  const primary = entry.records[0] || {};
  const headline = primary.description || 'Untitled transaction';
  const historyId = entry.history_record_id;

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    setHistoryError('');
    try {
      const response = await fetch(ENDPOINTS.EXPENSES_ENTRY_HISTORY(historyId), {
        headers: getAuthHeaders(),
      });
      if (handleAuthError(response)) return;
      if (!response.ok) throw new Error('Could not load history');
      setHistory(await response.json());
    } catch (err) {
      // The entry itself keeps rendering: a history outage must never be the
      // reason someone cannot recover their money.
      setHistoryError(err.message || 'Could not load history');
      setHistory(null);
    } finally {
      setHistoryLoading(false);
    }
  }, [historyId]);

  const toggleHistory = () => {
    const next = !historyOpen;
    setHistoryOpen(next);
    if (next && !history && !historyLoading) loadHistory();
  };

  const historyRegionId = `deleted-history-${entry.key}`;

  return (
    <li className="rounded-[22px] border border-slate-200 bg-white px-4 py-3.5 shadow-sm">
      <div className="flex flex-wrap items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">
        <span>{primary.occurred_on}</span>
        <span aria-hidden="true">•</span>
        <span>{primary.category || 'Uncategorized'}</span>
        <span aria-hidden="true">•</span>
        <span>{describeEntryType(primary)}</span>
      </div>

      <div className="mt-1.5 flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <h3 className="min-w-0 break-words text-base font-semibold text-slate-900">{headline}</h3>
        <span className="text-base font-semibold text-slate-900">
          {formatMoney(primary.currency, primary.total_cost || 0)}
        </span>
      </div>

      {/* Who it moved money between — the thing you actually need to tell two
          same-named dinners apart. */}
      <ul className="mt-2 space-y-0.5 text-sm text-slate-500">
        {(primary.shares || []).map((share) => (
          <li key={share.member.slug} className="flex items-center justify-between gap-3">
            <span>{share.member.display_name}</span>
            <span className="tabular-nums">
              {formatMoney(primary.currency, share.share_amount || 0)}
              <span className={clsx('ml-2 text-xs', Number(share.balance_delta) >= 0 ? 'text-emerald-600' : 'text-rose-600')}>
                {Number(share.balance_delta) >= 0 ? '+' : ''}{Number(share.balance_delta || 0).toFixed(2)}
              </span>
            </span>
          </li>
        ))}
      </ul>

      {entry.is_fx_pair ? (
        <p className="mt-2 flex items-start gap-1.5 rounded-xl bg-indigo-50 px-3 py-2 text-xs text-indigo-800 ring-1 ring-indigo-100">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          <span>
            Currency conversion — {entry.leg_count} linked rows. Restoring brings both legs back together;
            a single leg would leave the debt recorded twice, or not at all.
          </span>
        </p>
      ) : null}

      <p className="mt-2 text-xs text-slate-500">
        Deleted {formatHistoryStamp(deletionStamp(entry)) || 'at an unrecorded time'}
        {entry.deleted_by ? <> · {describeActor(entry.deleted_by)}</> : null}
      </p>
      {entry.deleted_by?.reason ? (
        <p className="mt-1 text-xs italic text-slate-500">“{entry.deleted_by.reason}”</p>
      ) : null}

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={toggleHistory}
          aria-expanded={historyOpen}
          aria-controls={historyRegionId}
          className="inline-flex min-h-[44px] items-center gap-1.5 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-600 transition hover:border-slate-300 hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-500"
        >
          <ChevronDown className={clsx('h-3.5 w-3.5 transition', historyOpen && 'rotate-180')} aria-hidden="true" />
          History
        </button>

        {confirming ? (
          <>
            <span className="text-sm font-medium text-slate-700">Put this back in the ledger?</span>
            <button
              type="button"
              ref={restoreButtonRef}
              data-restore-control
              disabled={disabled}
              onClick={() => { setConfirming(false); onRestore(entry); }}
              className="inline-flex min-h-[44px] items-center gap-1.5 rounded-full border-2 border-slate-900 bg-slate-900 px-4 py-2 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-500 focus-visible:ring-offset-2"
            >
              {restoring ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <RotateCcw className="h-4 w-4" aria-hidden="true" />}
              Confirm restore
            </button>
            <button
              type="button"
              onClick={() => setConfirming(false)}
              className="inline-flex min-h-[44px] items-center rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-600 transition hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-500"
            >
              Cancel
            </button>
          </>
        ) : (
          <button
            type="button"
            data-restore-control
            disabled={disabled}
            onClick={() => setConfirming(true)}
            // Named for the transaction, not "Restore" ×5: a screen reader
            // running the button list otherwise hears the same word repeatedly
            // with no way to tell which entry it belongs to.
            aria-label={`Restore ${headline}, ${formatMoney(primary.currency, primary.total_cost || 0)} from ${primary.occurred_on}`}
            className="inline-flex min-h-[44px] items-center gap-1.5 rounded-full border-2 border-slate-900 bg-slate-900 px-4 py-2 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-500 focus-visible:ring-offset-2"
          >
            {restoring ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <RotateCcw className="h-4 w-4" aria-hidden="true" />}
            Restore
          </button>
        )}
      </div>

      {historyOpen ? (
        <div id={historyRegionId} className="mt-3 border-t border-slate-100 pt-2">
          {historyLoading ? (
            <p className="mt-2 flex items-center gap-2 text-sm text-slate-500">
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Loading history…
            </p>
          ) : historyError ? (
            <div className="mt-2 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
              <p>{historyError}</p>
              <button type="button" onClick={loadHistory} className="mt-2 min-h-[44px] font-semibold underline">
                Try again
              </button>
            </div>
          ) : (
            <HistoryEventList events={history?.events} />
          )}
        </div>
      ) : null}
    </li>
  );
};

const RecentlyDeletedDrawer = ({
  open,
  onClose,
  listing,
  loading,
  error,
  onRetry,
  onRestore,
  backgroundRef,
}) => {
  const dialogRef = useRef(null);
  const [restoringKey, setRestoringKey] = useState(null);
  const [announcement, setAnnouncement] = useState('');
  // Which position in the list to put focus back on once a restore settles.
  // Null means "nobody is waiting on focus".
  const [focusPosition, setFocusPosition] = useState(null);
  const handleKeyDown = useDialogA11y({ open, onClose, dialogRef, backgroundRef });

  // Memoised so it is a stable dependency for the focus effect below.
  const entries = useMemo(() => listing?.entries || [], [listing]);

  useEffect(() => {
    if (!open) {
      setRestoringKey(null);
      setAnnouncement('');
      setFocusPosition(null);
    }
  }, [open]);

  /**
   * Catch the keyboard user a successful restore would otherwise drop.
   *
   * Restoring destroys the button that was focused -- the entry leaves the
   * list -- but the drawer STAYS OPEN, so `useDialogA11y` never gets a chance
   * to help: it only hands focus back when a dialog closes, and it rightly
   * refuses to focus a launcher that has left the document. Focus therefore
   * fell to <body>, inside an open modal, with the whole page behind it inert.
   * Tab from there resumed from nowhere: a keyboard user was stranded in the
   * middle of recovering their own money.
   *
   * So place it deliberately: the recovery control that now occupies the slot
   * the restored entry vacated (the next one down, or the last if it was the
   * bottom of the list), and the drawer heading when there is nothing left to
   * restore -- which is also where the "nothing deleted" message now is.
   */
  useEffect(() => {
    if (!open || focusPosition === null) return;
    const dialog = dialogRef.current;
    if (!dialog) return;
    const controls = Array.from(dialog.querySelectorAll('[data-restore-control]'))
      .filter((el) => !el.disabled);
    const next = controls.length ? controls[Math.min(focusPosition, controls.length - 1)] : null;
    (next || dialog.querySelector('[data-dialog-heading]') || dialog)?.focus();
    setFocusPosition(null);
  }, [open, focusPosition, entries]);

  if (!open) return null;

  const restore = async (entry) => {
    // Captured before the list changes underneath us.
    const position = Math.max(0, entries.findIndex((candidate) => candidate.key === entry.key));
    setRestoringKey(entry.key);
    setAnnouncement('');
    try {
      const result = await onRestore(entry);
      setAnnouncement(
        result?.alreadyRestored
          ? 'That entry was already back in the ledger.'
          : `Restored ${entry.records[0]?.description || 'entry'}.`
      );
    } catch (err) {
      setAnnouncement(err?.message || 'Could not restore that entry.');
    } finally {
      // In `finally` on purpose: a FAILED restore also destroys the focused
      // control, because the confirm step collapses back to the plain Restore
      // button. Either way somebody has to be given focus.
      setRestoringKey(null);
      setFocusPosition(position);
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-slate-900/40 p-0 backdrop-blur-sm sm:items-center sm:p-6"
      onClick={onClose}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="recently-deleted-title"
        onKeyDown={handleKeyDown}
        onClick={(event) => event.stopPropagation()}
        className="flex max-h-[92vh] w-full max-w-xl flex-col overflow-hidden rounded-t-[28px] bg-white shadow-2xl sm:rounded-[28px]"
      >
        <header className="flex items-start justify-between gap-3 border-b border-slate-100 px-5 py-4">
          <div className="min-w-0">
            <h2
              id="recently-deleted-title"
              data-dialog-heading
              tabIndex={-1}
              className="text-lg font-semibold text-slate-900 focus:outline-none"
            >
              Recently deleted
            </h2>
            <p className="mt-1 text-sm text-slate-500">
              Deleted entries count for nothing — not balances, spending, categories, search or the
              exported ledger — until you put one back. Nothing here is ever removed permanently.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close recently deleted"
            className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-slate-200 text-slate-500 transition hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-500"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto overflow-x-hidden px-5 py-4">
          {/* Restore outcomes are announced, not just toasted: the toast lives
              outside the dialog a screen-reader user is currently inside. */}
          <p aria-live="polite" role="status" className="sr-only">{announcement}</p>

          {loading ? (
            <p className="flex items-center gap-2 text-sm text-slate-500">
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Loading deleted entries…
            </p>
          ) : error ? (
            <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
              <p>{error}</p>
              <button type="button" onClick={onRetry} className="mt-2 min-h-[44px] font-semibold underline">
                Try again
              </button>
            </div>
          ) : entries.length === 0 ? (
            <p className="text-sm text-slate-500">Nothing has been deleted. There is nothing to recover.</p>
          ) : (
            <>
              <ul className="space-y-3">
                {entries.map((entry) => (
                  <DeletedEntry
                    key={entry.key}
                    entry={entry}
                    onRestore={restore}
                    restoring={restoringKey === entry.key}
                    disabled={restoringKey !== null && restoringKey !== entry.key}
                  />
                ))}
              </ul>
              {listing?.truncated ? (
                <p className="mt-3 text-xs text-slate-500">
                  Showing the {entries.length} most recently deleted of {listing.total}.
                </p>
              ) : null}
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default RecentlyDeletedDrawer;
